/**
 * PopupEventsPage - Stateless events viewer popup
 *
 * Displays events list with analytics (active/deactivated counts, by severity, by detector).
 * Shows event table with same kebab actions as main Events panel.
 * Receives all data from the main window via BroadcastChannel.
 * Does NOT create its own WebSocket connections.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  Button,
  Card,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DateTimePickerWithRange,
} from '@ais-ui/ui-core';
import { useTimeZoneContext, formatTimeWithTimeZone } from '@ais-ui/utils';
import { useDebounce } from '@ais-ui/custom-hooks';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { X } from 'lucide-react';
import { PiClockCounterClockwise, PiCalendar } from 'react-icons/pi';
import { PiFileCsv } from 'react-icons/pi';
import {
  PopupLoadingState,
  PopupErrorState,
  usePopupTableSort,
  usePopupCsvExport,
  getDensityClasses,
} from './shared';
import {
  PopupType,
  getPopupIdFromUrl,
  getPopupParams,
  closePopup,
} from '@ais-ui/popups';
import {
  PopupActions,
  MessageType,
  useMainWindowMessages,
  PopupMessage,
  DataType,
} from './PopupEvents';
import { usePopupLifecycle } from './usePopupLifecycle';
import { PopupConnectionStatus } from './shared/PopupConnectionStatus';
import { usePopupPerformanceTracking } from './usePopupPerformanceTracking';
import type { DetectedEvent } from '@ais-ui/websockets';
import { DateRange } from 'react-day-picker';
import { EventsTimelineChart } from './EventsTimelineChart';
import { EventsTableSection, mapSeverity } from './EventsTableSection';

/**
 * Data structure sent from main window
 */
export interface PopupEventsData {
  events: DetectedEvent[];
  totalEvents: number;
  activeEvents: number;
  deactivatedEvents: number;
  dateRange: {
    from: string;
    to: string;
  };
  isTrailing: boolean;
  settings?: {
    tableDensity: 'normal' | 'compact';
    showEventGraph?: boolean;
  };
  /** Unique key for this data snapshot - used to determine if update is needed */
  dataKey: string;
}

export const PopupEventsPage: React.FC = () => {
  const popupId = getPopupIdFromUrl();
  const params = getPopupParams();
  const { formatDateTimeWithTimeZone, userDateFormat } = useDateTimeFormat();
  const { timeZone } = useTimeZoneContext();
  const { messages } = useLocalization();

  const [data, setData] = useState<PopupEventsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dataReceivedRef = useRef(false);
  const dataKeyRef = useRef<string | undefined>(undefined);

  // Performance tracking
  const { trackDataRequest, trackDataReceived, trackError, trackTimeout } =
    usePopupPerformanceTracking({
      popupId,
      popupType: PopupType.Events,
    });

  // Local state for date range (synced with main window)
  const [localDate, setLocalDate] = useState<DateRange | undefined>(undefined);
  const [localIsTrailing, setLocalIsTrailing] = useState(true);

  // Filters
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [detectorFilter, setDetectorFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(
    search.length >= 3 ? search : '',
    200,
    '',
  );

  // Table density from settings (default to compact)
  const tableDensity = data?.settings?.tableDensity || 'compact';
  const densityClasses = getDensityClasses(tableDensity);

  // Event graph visibility from settings (default to true)
  const showEventGraph = data?.settings?.showEventGraph ?? true;
  const [isChartMinimized, setIsChartMinimized] = useState(false);

  // Sorting state using shared hook
  const { orderBy, orderAscending, handleSort } = usePopupTableSort({
    defaultOrderBy: 'act_ts',
    defaultAscending: false,
  });

  // Pagination
  const [page, setPage] = useState(1);
  const perPage = 50;

  // Sync local date state when data arrives from main window
  useEffect(() => {
    if (data?.dateRange) {
      setLocalDate({
        from: data.dateRange.from ? new Date(data.dateRange.from) : undefined,
        to: data.dateRange.to ? new Date(data.dateRange.to) : undefined,
      });
      setLocalIsTrailing(data.isTrailing);
    }
  }, [data?.dateRange?.from, data?.dateRange?.to, data?.isTrailing]);

  // Handle date change - send to main window (user manually changed date, so disable trailing)
  const handleDateChange: React.Dispatch<
    React.SetStateAction<DateRange | undefined>
  > = useCallback(
    (value) => {
      const currentDate = localDate;
      const newDate = typeof value === 'function' ? value(currentDate) : value;
      setLocalDate(newDate);
      setLocalIsTrailing(false); // Manual date change disables trailing
      PopupActions.setEventDateRange(
        newDate?.from?.toISOString() || '',
        newDate?.to?.toISOString() || '',
        false,
      );
    },
    [localDate],
  );

  // Handle trailing toggle - send to main window
  const handleTrailingChange = useCallback(
    (newIsTrailing: boolean) => {
      setLocalIsTrailing(newIsTrailing);
      PopupActions.setEventDateRange(
        localDate?.from?.toISOString() || '',
        localDate?.to?.toISOString() || '',
        newIsTrailing,
      );
    },
    [localDate],
  );

  // Request initial data from main window
  useEffect(() => {
    if (!popupId) {
      setError('No popup ID found');
      setIsLoading(false);
      trackError('No popup ID found');
      return;
    }

    dataReceivedRef.current = false;
    trackDataRequest();

    PopupActions.requestInit({
      popupId,
      popupType: PopupType.Events,
      params,
    });

    const timeout = setTimeout(() => {
      if (!dataReceivedRef.current) {
        setError('Timeout waiting for data from main window.');
        setIsLoading(false);
        trackTimeout();
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [popupId, trackDataRequest, trackError, trackTimeout]);

  // Keep dataKeyRef in sync with current data
  useEffect(() => {
    dataKeyRef.current = data?.dataKey;
  }, [data?.dataKey]);

  // Listen for messages from main window
  useMainWindowMessages(
    (msg: PopupMessage) => {
      if (
        msg.type === MessageType.POPUP_INIT_RESPONSE &&
        msg.popupId === popupId
      ) {
        dataReceivedRef.current = true;
        const payload = msg.payload as PopupEventsData;
        dataKeyRef.current = payload.dataKey;
        setData(payload);
        setIsLoading(false);
        trackDataReceived(payload.events?.length);
        document.title = 'Events Overview';
      }

      if (msg.type === MessageType.DATA_UPDATE) {
        const payload = msg.payload as {
          dataType: string;
          data: PopupEventsData;
        };
        if (payload.dataType === DataType.EVENTS) {
          // Only update if dataKey is different to avoid unnecessary re-renders
          if (payload.data.dataKey !== dataKeyRef.current) {
            dataKeyRef.current = payload.data.dataKey;
            setData(payload.data);
          }
        }
      }

      if (msg.type === MessageType.SESSION_ENDED) {
        setError('Session ended. Please close this window.');
        trackError('Session ended');
      }
    },
    popupId ?? undefined,
    [popupId, trackDataReceived, trackError],
  );

  // Handle popup lifecycle (close notification, heartbeat, connection tracking)
  const connectionState = usePopupLifecycle(popupId, PopupType.Events);

  // Get unique detectors for filter dropdown
  const uniqueDetectors = useMemo(() => {
    if (!data?.events) return [];
    const detectors = new Set(data.events.map((e) => e.name).filter(Boolean));
    return Array.from(detectors).sort();
  }, [data?.events]);

  // Filter and sort events
  const filteredAndSortedEvents = useMemo(() => {
    if (!data?.events) return [];

    let filtered = data.events;

    // Apply search filter (same logic as EventList)
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.name?.toLowerCase().includes(searchLower) ||
          e.shipName?.toLowerCase().includes(searchLower) ||
          e.mmsi?.toString().includes(searchLower),
      );
    }

    // Apply severity filter
    if (severityFilter !== 'all') {
      filtered = filtered.filter(
        (e) => mapSeverity(e.severity) === severityFilter,
      );
    }

    // Apply detector filter
    if (detectorFilter !== 'all') {
      filtered = filtered.filter((e) => e.name === detectorFilter);
    }

    // Sort
    return [...filtered].sort((a, b) => {
      let aVal: unknown = a[orderBy as keyof DetectedEvent];
      let bVal: unknown = b[orderBy as keyof DetectedEvent];

      // Handle date fields
      if (orderBy === 'act_ts' || orderBy === 'deactivated') {
        aVal = aVal ? new Date(aVal as string | Date).getTime() : 0;
        bVal = bVal ? new Date(bVal as string | Date).getTime() : 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return orderAscending
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      const aNum = Number(aVal) || 0;
      const bNum = Number(bVal) || 0;
      return orderAscending ? aNum - bNum : bNum - aNum;
    });
  }, [
    data?.events,
    orderBy,
    orderAscending,
    severityFilter,
    detectorFilter,
    debouncedSearch,
  ]);

  // Paginate events
  const paginatedEvents = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredAndSortedEvents.slice(start, start + perPage);
  }, [filteredAndSortedEvents, page, perPage]);

  const totalPages = Math.ceil(filteredAndSortedEvents.length / perPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [severityFilter, detectorFilter, debouncedSearch]);

  // Generate timeline chart data (events over time)
  // Only recalculate when dataKey changes (indicates actual data change)
  const timelineChartData = useMemo(() => {
    if (!data?.events?.length || !data.dateRange.from || !data.dateRange.to)
      return [];

    const from = new Date(data.dateRange.from);
    const to = new Date(data.dateRange.to);
    const rangeMs = to.getTime() - from.getTime();
    if (rangeMs <= 0) return [];

    // The chart samples how many events are concurrently active at each bucket's
    // start instant. Sampling too coarsely makes short-lived events fall between
    // samples: e.g. one-bucket-per-day over a month only checks concurrency at
    // midnight, so a window full of short events reads as mostly zero. To keep
    // long ranges as legible as short ones, aim for a consistent number of
    // samples regardless of range (never finer than 5 minutes).
    const TARGET_BUCKETS = 96;
    const MIN_BUCKET_MS = 5 * 60 * 1000;
    const bucketMs = Math.max(
      MIN_BUCKET_MS,
      Math.ceil(rangeMs / TARGET_BUCKETS),
    );
    const bucketCount = Math.ceil(rangeMs / bucketMs);

    // Create buckets
    const buckets: { time: Date; active: number }[] = [];
    for (let i = 0; i < bucketCount; i++) {
      const bucketTime = new Date(from.getTime() + i * bucketMs);
      buckets.push({ time: bucketTime, active: 0 });
    }

    // Count active events at each bucket time.
    // Use `deactivated` (null while an event is active) as the canonical end
    // timestamp — same field the table, CSV export and summary counts use.
    // `deact_ts` is unreliable for still-active events and made them read as
    // already-ended, so the chart under-counted (or showed zero when every
    // event was active).
    data.events.forEach((event) => {
      const actTs = new Date(event.act_ts).getTime();
      const deactTs = event.deactivated
        ? new Date(event.deactivated).getTime()
        : to.getTime();

      buckets.forEach((bucket) => {
        const bucketTime = bucket.time.getTime();
        if (actTs <= bucketTime && bucketTime <= deactTs) {
          bucket.active++;
        }
      });
    });

    return buckets.map((b) => ({
      time: formatTimeWithTimeZone(b.time, timeZone, userDateFormat, true),
      fullTime: b.time.toISOString(),
      active: b.active,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.dataKey]);

  // CSV export using shared hook
  const { exportToCsv } = usePopupCsvExport({
    data: data?.events,
    headers: [
      'name',
      'shipName',
      'mmsi',
      'severity',
      'act_ts',
      'deactivated',
      'ack_state',
    ] as (keyof DetectedEvent)[],
    filenamePrefix: 'events',
  });

  if (error) {
    return (
      <PopupErrorState
        message={error}
        title={messages.popupGenericError}
        closeButtonText={messages.popupGenericCloseWindow}
      />
    );
  }

  if (isLoading || !data) {
    return <PopupLoadingState message={messages.popupEventsLoading} />;
  }

  return (
    <div className="flex flex-col h-screen bg-gradient-to-b from-primary/5 via-background to-background">
      {/* Connection status overlay */}
      <PopupConnectionStatus connectionState={connectionState} />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">{messages.popupEventsTitle}</h1>
          <p className="text-sm text-muted-foreground">
            {data.totalEvents} events ({data.activeEvents} active,{' '}
            {data.deactivatedEvents} deactivated)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportToCsv}>
            <PiFileCsv className="mr-2 h-4 w-4" />{' '}
            {messages.popupGenericExportCsv}
          </Button>
          <Button variant="ghost" size="icon" onClick={closePopup}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden p-4 gap-4">
        {/* Controls Row: Date picker - no local trailing timer, just display and allow changes */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <DateTimePickerWithRange
            className="w-full"
            date={localDate}
            setDate={handleDateChange}
            timeZone={timeZone}
            dateTimeFormat={userDateFormat}
            dateLimit="past"
            disabled={localIsTrailing}
            showPresets
            defaultFromTime={{ hours: 0, minutes: 0 }}
            defaultToTime="now"
            onPresetSelect={(from, to) => {
              setLocalDate({ from, to });
              setLocalIsTrailing(false);
              PopupActions.setEventDateRange(
                from.toISOString(),
                to.toISOString(),
                false,
              );
            }}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                onClick={() => handleTrailingChange(!localIsTrailing)}
              >
                {localIsTrailing ? (
                  <PiCalendar className="size-5" />
                ) : (
                  <PiClockCounterClockwise className="size-5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {localIsTrailing
                ? messages.popupEventsShowFixedPeriod
                : messages.popupEventsTrailEvents}
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Summary Cards */}
        <Card className="p-4 flex-shrink-0">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">
                {messages.popupEventsTotalEvents}
              </span>
              <p className="text-2xl font-semibold">{data.totalEvents}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">
                {messages.popupEventsActive}
              </span>
              <p className="text-2xl font-semibold text-blue-600">
                {data.activeEvents}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">
                {messages.popupEventsDeactivated}
              </span>
              <p className="text-2xl font-semibold text-muted-foreground">
                {data.deactivatedEvents}
              </p>
            </div>
          </div>
        </Card>

        {/* Timeline Chart */}
        {showEventGraph && timelineChartData.length > 0 && (
          <EventsTimelineChart
            data={timelineChartData}
            isMinimized={isChartMinimized}
            onMinimizedChange={setIsChartMinimized}
            formatDateTime={formatDateTimeWithTimeZone}
            messages={messages}
          />
        )}

        {/* Table */}
        <EventsTableSection
          events={paginatedEvents}
          totalFilteredCount={filteredAndSortedEvents.length}
          search={search}
          onSearchChange={setSearch}
          severityFilter={severityFilter}
          onSeverityFilterChange={setSeverityFilter}
          detectorFilter={detectorFilter}
          onDetectorFilterChange={setDetectorFilter}
          uniqueDetectors={uniqueDetectors}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          orderBy={orderBy}
          orderAscending={orderAscending}
          onSort={handleSort}
          isFiltered={
            severityFilter !== 'all' ||
            detectorFilter !== 'all' ||
            !!debouncedSearch
          }
          densityClasses={densityClasses}
          formatDateTime={formatDateTimeWithTimeZone}
          messages={messages}
        />
      </div>
    </div>
  );
};

export default PopupEventsPage;
